import Http from '@/services/http';
import serviceFields
  from '@/domain/data/installation/engineering/coolingMaintenance/coolingMaintenanceServiceFields.json';
import translate from '@/services/translate';
import { grammaticalJoin } from '@/services/arrayUtils';
import {
  CoolingMaintenanceStatus,
  CoolingMaintenanceStatusMaintenance,
  OperatingInformation,
  CoolingMaintenanceGroupStatus,
  CoolingMaintenanceInformMaintenanceRequest,
  CoolingMaintenanceReplaceGroupRequest,
  CoolingMaintenanceParameters,
  CoolingMaintenanceGroupDetails,
  CoolingMaintenancePostGroupRequest,
  CoolingMaintenancePutGroupRequest,
  Lookup,
} from 'sigma-mappings';

/* istanbul ignore next */
const errors = {
  errorHandling: {
    responseMessageParams: {
      $MA001: (data) => {
        const parameters = Object.values(data.parameters) || [];
        return {
          NomeDoCampo: grammaticalJoin(parameters.map(
            field => translate(serviceFields[field]),
          )),
        };
      },
      $MA029: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = serviceFields[data.parameters[2]];
        return {
          FaixaInicial: data.parameters[0],
          FaixaFinal: data.parameters[1],
          NomeDoCampo: translate(returnedField),
        };
      },
      $MA039: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = serviceFields[data.parameters[0]];
        return {
          field: translate(returnedField),
        };
      },
      $MA051: (data) => {
        const returnedField = serviceFields[data.parameters[1]];
        if (!returnedField) return null;
        return {
          maxLength: data.parameters[0],
          fieldName: translate(returnedField),
        };
      },
      $MA098: (data) => {
        if (!data && !data.field) return null;
        const returnedField = serviceFields[data.parameters[0]];
        return {
          0: translate(returnedField),
        };
      },
      $MA099: (data) => {
        if (!data && !data.field) return null;
        return {
          0: data.parameters[0],
          1: data.parameters[1],
          2: data.parameters[2],
          3: data.parameters[3],
        };
      },
    },
  },
};

const urls = {
  getStatus: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/status`,
  getMaintenanceStatus: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/status-manutencao`,
  getOperatingInformation: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/informacoes-funcionamento`,
  getGroupStatus: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/status-grupos`,
  postReportMaintenance: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/informar-manutencao`,
  postReplaceGroup: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/substituir-grupo`,
  getParameters: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/parametros`,
  getParametersByGroupId: (assetModuleId, groupId) => `/engenharia/${assetModuleId}/manutencao-resfriamento/parametros/${groupId}`,
  getAssociatedContacts: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/contatos-associados`,
  getGroups: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/grupos`,
  postGroup: assetModuleId => `/engenharia/${assetModuleId}/manutencao-resfriamento/grupos`,
  putGroupById: (assetModuleId, groupId) => `/engenharia/${assetModuleId}/manutencao-resfriamento/grupos/${groupId}`,
  deleteGroupById: (assetModuleId, groupId) => `/engenharia/${assetModuleId}/manutencao-resfriamento/grupos/${groupId}`,
};

export default {
  getStatus(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getStatus(assetModuleId))
      .then(({ data }) => new CoolingMaintenanceStatus(data));
  },
  getMaintenanceStatus(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getMaintenanceStatus(assetModuleId))
      .then(({ data }) => data.map(item => new CoolingMaintenanceStatusMaintenance(item)));
  },
  getOperatingInformationCharts(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getOperatingInformation(assetModuleId))
      .then(({ data }) => data.map(item => new OperatingInformation(item)));
  },
  getGroupStatus(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getGroupStatus(assetModuleId))
      .then(({ data }) => data.map(item => new CoolingMaintenanceGroupStatus(item)));
  },
  reportMaintenance(assetModuleId, payload) {
    const http = new Http(errors);
    const mappedPayload = new CoolingMaintenanceInformMaintenanceRequest(payload);
    return http.post(urls.postReportMaintenance(assetModuleId), mappedPayload);
  },
  replaceGroup(assetModuleId, payload) {
    const http = new Http(errors);
    const mappedPayload = new CoolingMaintenanceReplaceGroupRequest(payload);
    return http.post(urls.postReplaceGroup(assetModuleId), mappedPayload);
  },
  getParameters(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getParameters(assetModuleId))
      .then(({ data }) => data.map(item => new CoolingMaintenanceParameters(item)));
  },
  getGroupById(assetModuleId, groupId) {
    const http = new Http(errors);
    return http.get(urls.getParametersByGroupId(assetModuleId, groupId))
      .then(({ data }) => new CoolingMaintenanceGroupDetails(data));
  },
  getAssociatedContacts(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getAssociatedContacts(assetModuleId))
      .then(({ data }) => data.map(item => new Lookup(item)));
  },
  getGroups(assetModuleId) {
    const http = new Http(errors);
    return http.get(urls.getGroups(assetModuleId))
      .then(({ data }) => data.map(item => new Lookup(item)));
  },
  setGroup(assetModuleId, payload) {
    const http = new Http(errors);
    const mappedPayload = new CoolingMaintenancePostGroupRequest(payload);
    return http.post(urls.postGroup(assetModuleId), mappedPayload);
  },
  updateGroupById(assetModuleId, groupId, payload) {
    const http = new Http(errors);
    const mappedPayload = new CoolingMaintenancePutGroupRequest(payload);
    return http.put(urls.putGroupById(assetModuleId, groupId), mappedPayload);
  },
  deleteGroupById(assetModuleId, groupId) {
    const http = new Http(errors);
    return http.delete(urls.deleteGroupById(assetModuleId, groupId));
  },
};
